import { useEffect } from "react";

import { DateRange } from "@app/analytics/meetops/types";
import {
  PROD_SAMPLE_MEETING,
  STAGING_SAMPLE_MEETING,
} from "@app/analytics/sampleData/sampleMeetings";
import useMeetingReportsStore from "@app/analytics/stores/useMeetingReportsStore";
import type { ReportListView } from "@app/analytics/types";
import useHasMeetings from "@app/common/hooks/meetings/useHasMeetings";
import { User, UserMeeting } from "@app/common/types";
import { EnterpriseTeam } from "@app/common/workspaces/types";

import useSessionsListQuery from "./useSessionsListQuery";

const TWO_MINUTES = 2 * 60 * 1000;

interface UseMeetingsQueryOpts {
  dateRanges: DateRange[];
  workspaceId?: string;
  teamId?: EnterpriseTeam["id"];
  userId?: User["id"];
  includeSampleMeetings?: boolean;
  view?: ReportListView;
  recurringEventId?: string | null;
}

const useMeetings = (
  {
    dateRanges,
    workspaceId,
    teamId,
    userId,
    includeSampleMeetings = false,
    view = "default",
    recurringEventId,
  }: UseMeetingsQueryOpts,
  options?: {
    enabled?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnMount?: boolean;
    refetchInterval?: number;
    staleTime?: number;
  }
) => {
  const setIsFetching = useMeetingReportsStore((state) => state.setIsFetching);

  const sampleMeeting: UserMeeting =
    import.meta.env.VITE_APP_STAGE === "production"
      ? PROD_SAMPLE_MEETING
      : STAGING_SAMPLE_MEETING;

  const { hasEstablishedMeetings, isLoading: isMeetingCountLoading } =
    useHasMeetings({
      userId,
      teamId,
      // don't bother fetching the meeting count if we're not going to show
      // sample meetings from this instance
      enabled: (options?.enabled ?? true) && includeSampleMeetings,
    });

  const sessionsListQuery = useSessionsListQuery(
    {
      ownerUserId: userId,
      ownerTeamId: teamId,
      ownerWorkspaceId: teamId || userId ? undefined : workspaceId,
      startDate: dateRanges[0].startDate,
      endDate: dateRanges[0].endDate,
      view: view === "default" ? null : view,
      recurringEventId: recurringEventId,
    },
    {
      refetchOnMount: options?.refetchOnMount ?? false,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
      refetchInterval: options?.refetchInterval ?? TWO_MINUTES,
      enabled: options?.enabled ?? true,
      staleTime: options?.staleTime,
      ...options,
    }
  );

  useEffect(() => {
    setIsFetching(sessionsListQuery.isFetching);
  }, [sessionsListQuery.isFetching]);

  let meetingsForDateRange: UserMeeting[] = sessionsListQuery.data ?? [];

  const showSampleMeetings = includeSampleMeetings && !hasEstablishedMeetings;

  const progress = meetingsForDateRange.length;
  if (showSampleMeetings) {
    meetingsForDateRange = [...meetingsForDateRange, sampleMeeting];
  }

  return {
    meetings: meetingsForDateRange,
    showSampleMeetings,
    progress,
    isLoading: sessionsListQuery.isLoading || isMeetingCountLoading,
    isError: sessionsListQuery.isError,
    refetch: sessionsListQuery.refetch,
  };
};

export default useMeetings;
